import {
  Code,
  Globe,
  Hash,
  Layers,
  type LucideIcon,
  MapPin,
  ListChecks,
  MessageCircle,
  OctagonAlert,
  Send,
  ShieldAlert,
  Table2,
  ToggleRight,
  Users
} from 'lucide-react';

export type AdminSectionSlug =
  | 'users'
  | 'languages'
  | 'topics'
  | 'core-risks'
  | 'locations'
  | 'delivery'
  | 'queues'
  | 'prompts'
  | 'classifications'
  | 'escalation'
  | 'customer-feedback-sheets'
  | 'feedback'
  | 'switches';

export type AdminSection = {
  readonly slug: AdminSectionSlug;
  readonly label: string;
  readonly hint: string;
  readonly icon: LucideIcon;
  // Reachable by analyst_admin too. Every other section reads AdminConfigController, which is
  // admin-only; Classifications serves analyst_admin the toggle-only taxonomy view instead.
  readonly broadViewOnly?: boolean;
  // Shown, not edited, here (pack-owned reference data, compiled prompts): the sidebar marks it
  // with a lock.
  readonly readOnly?: boolean;
};

// Each settings section is its own page at /admin/<slug>, placed in the sidebar by nav.ts, so
// a link to one (e.g. /admin/prompts) can be shared. One list drives the nav, the routes and the
// page titles.
export const ADMIN_SECTIONS: readonly AdminSection[] = [
  {
    slug: 'users',
    label: 'Users',
    hint: 'Manage vertical access and P0 approval settings',
    icon: Users
  },
  {
    slug: 'languages',
    label: 'Languages',
    hint: 'Available languages for classification enrichment',
    icon: Globe,
    readOnly: true
  },
  {
    slug: 'topics',
    label: 'Topics',
    hint: 'Thematic topics to tag classifications with',
    icon: Hash
  },
  {
    slug: 'core-risks',
    label: 'Core risks',
    hint: 'Specific risk types mapped to classification categories',
    icon: ShieldAlert,
    readOnly: true
  },
  {
    slug: 'locations',
    label: 'Locations',
    hint: 'Hierarchical location list for geographic tagging',
    icon: MapPin,
    readOnly: true
  },
  {
    slug: 'delivery',
    label: 'Delivery',
    hint: 'Delivery hoppers, their targets and batch schedules',
    icon: Send
  },
  {
    slug: 'queues',
    label: 'Queues',
    hint: 'Moderation queue access, batch sizes and lease timings',
    icon: ListChecks
  },
  {
    slug: 'prompts',
    label: 'Prompts',
    hint: 'View prompts compiled from Langfuse',
    icon: Code,
    readOnly: true
  },
  {
    slug: 'classifications',
    label: 'Classifications',
    hint: 'Manage verticals and risk categories for classifying',
    icon: Layers,
    broadViewOnly: true
  },
  {
    slug: 'escalation',
    label: 'Escalation',
    hint: 'Escalation variants and contact rules that drive P0 alert recipients',
    icon: OctagonAlert
  },
  {
    slug: 'customer-feedback-sheets',
    label: 'Customer feedback sheets',
    hint: "The customer's feedback spreadsheets PRI reads daily for review on the submissions page",
    icon: Table2
  },
  {
    slug: 'feedback',
    label: 'Analyst feedback',
    hint: 'Feedback analysts have submitted from the app',
    icon: MessageCircle
  },
  {
    slug: 'switches',
    label: 'Capability switches',
    hint: 'Turn capabilities off instantly, or propose rollout changes for review',
    icon: ToggleRight
  }
];

export function adminSection(slug: string): AdminSection | undefined {
  return ADMIN_SECTIONS.find((section) => section.slug === slug);
}

export function adminSectionPath(slug: AdminSectionSlug): string {
  return `/admin/${slug}`;
}
